/**
 * Quote slice — the quotes board.
 *
 * Holds only what the board itself needs: the list, and the two write actions that
 * change which lane a card is in. A quote's DETAIL is a job's detail — the modal, the
 * work items, the notes all run through `jobSlice`, because a quote is a job on the
 * server and a second copy of that state would have to be kept in step with it.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { readPage } from '../../utils/pageShape';
import {
    listQuotesApi, listOpenQuotesApi, listAwaitingQuotesApi, createQuoteApi, sendQuoteForApprovalApi,
    OPEN_QUOTES_PAGE_SIZE,
} from '../../services/quoteApi';

export const fetchQuotes = createAsyncThunk(
    'quotes/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await listQuotesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load quotes');
        }
    }
);

/**
 * The not-yet-sent lane: a page of twenty from the server, searched there once the
 * typing settles and narrowed locally in between.
 *
 * 🔑 Separate from `fetchQuotes`, which the PHONE still uses as one list. The desktop
 * board is where a migration's thousands of unanswered quotes land, and that lane is
 * the one that needs a page and a search — see listOpenQuotesApi.
 */
export const fetchOpenQuotes = createAsyncThunk(
    'quotes/fetchOpen',
    async ({ q = '', page = 0, size = OPEN_QUOTES_PAGE_SIZE } = {}, { rejectWithValue }) => {
        try {
            return await listOpenQuotesApi({ q, page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load quotes');
        }
    }
);

/** The waiting-on-customer lane: the whole chase list, unfiltered, unpaged. */
export const fetchAwaitingQuotes = createAsyncThunk(
    'quotes/fetchAwaiting',
    async (_, { rejectWithValue }) => {
        try {
            return await listAwaitingQuotesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load quotes');
        }
    }
);

export const createQuote = createAsyncThunk(
    'quotes/create',
    async (data, { rejectWithValue }) => {
        try {
            return await createQuoteApi(data);
        } catch (error) {
            // The server names what is wrong — a vehicle that belongs to someone else,
            // a customer that no longer exists. A generic message would throw away the
            // only sentence that tells staff what to do next.
            return rejectWithValue(error.response?.data?.message || 'Failed to create quote');
        }
    }
);

export const sendQuoteForApproval = createAsyncThunk(
    'quotes/sendForApproval',
    async (quoteUuid, { rejectWithValue }) => {
        try {
            return await sendQuoteForApprovalApi(quoteUuid);
        } catch (error) {
            // Two refusals reach here and both need their own words: no email address
            // on the customer, and no priced work items on the quote.
            return rejectWithValue(error.response?.data?.message || 'Failed to send quote for approval');
        }
    }
);

const initialState = {
    quotes: [],
    loading: false,
    error: null,
    /** The not-yet-sent lane, one page of it. `total` is the server's totalSize. */
    open: {
        quotes: [],
        total: 0,
        page: 0,
        totalPages: 0,
        loading: false,
    },
    /** The waiting-on-customer lane, all of it. */
    awaiting: {
        quotes: [],
        loading: false,
    },
    // Distinct from `loading`: the board stays on screen and readable while a send is
    // in flight, so the two states cannot share one flag.
    operationLoading: false,
    operationError: null,
    lastCreated: null,
};

const quoteSlice = createSlice({
    name: 'quotes',
    initialState,
    reducers: {
        clearQuoteError: (state) => {
            state.error = null;
            state.operationError = null;
        },
        clearLastCreated: (state) => {
            state.lastCreated = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchQuotes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchQuotes.fulfilled, (state, action) => {
                state.loading = false;
                // The endpoint returns a plain list, not a page — outstanding quotes are
                // a workshop's chase list, not an archive.
                state.quotes = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchQuotes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchOpenQuotes.pending, (state) => {
                state.open.loading = true;
                state.error = null;
            })
            .addCase(fetchOpenQuotes.fulfilled, (state, action) => {
                // 🪤 Read through readPage: a Micronaut Page sends no totalPages or
                // pageNumber, and reading them left this lane's next button disabled at
                // "1–20 of 973". The jobs board had the same bug from the same cause.
                const { content, total, page, totalPages } = readPage(action.payload, {
                    size: action.meta?.arg?.size ?? OPEN_QUOTES_PAGE_SIZE,
                    page: action.meta?.arg?.page ?? 0,
                });
                state.open.loading = false;
                state.open.quotes = content;
                state.open.total = total;
                state.open.page = page;
                state.open.totalPages = totalPages;
            })
            .addCase(fetchOpenQuotes.rejected, (state, action) => {
                state.open.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchAwaitingQuotes.pending, (state) => {
                state.awaiting.loading = true;
                state.error = null;
            })
            .addCase(fetchAwaitingQuotes.fulfilled, (state, action) => {
                state.awaiting.loading = false;
                state.awaiting.quotes = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchAwaitingQuotes.rejected, (state, action) => {
                state.awaiting.loading = false;
                state.error = action.payload;
            })

            .addCase(createQuote.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createQuote.fulfilled, (state, action) => {
                state.operationLoading = false;
                // The job DETAIL comes back, so the caller can open it straight into
                // the work-items view. The board refetches rather than pushing this
                // shape into the list — a detail and a summary are different objects.
                state.lastCreated = action.payload;
            })
            .addCase(createQuote.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            .addCase(sendQuoteForApproval.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(sendQuoteForApproval.fulfilled, (state) => {
                state.operationLoading = false;
            })
            .addCase(sendQuoteForApproval.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });
    },
});

export const { clearQuoteError, clearLastCreated } = quoteSlice.actions;
export default quoteSlice.reducer;
